<template>
  <ProjectScaffold :loading="loadingProductionSteps">
    <div class="header">
      <div class="titles">
        <div class="title">Access The Production</div>
        <div class="subtitle">
          Please add your filestage project link in this section to help other producers find it easily
        </div>
      </div>
      <div class="actions">
        <CreateProductionSteps v-if="isProjectManager" :users="users" @input="handleStepsCreate" />
      </div>
    </div>
    <ts-divider></ts-divider>
    <div v-if="productionSteps.length > 0" class="list-actions">
      <div class="select">
        <ts-link @click="handleSelectAllClick">Select all</ts-link>
        <ts-link @click="handleUnselectAllClick">Unselect all</ts-link>
      </div>
      <visible :when="isProjectManager" class="actions">
        <ts-button size="small" filled @click="handleDeleteAllClick"> Delete </ts-button>
      </visible>
    </div>
    <div v-if="productionSteps.length > 0" class="list">
      <div class="list-header">
      </div>
      <div v-for="(step, i) in productionSteps" :key="i" class="row">
        <ts-checkbox
          class="checkbox"
          :value="isStepSelected(step)"
          @input="handleStepSelectedChange(step, $event)"
        ></ts-checkbox>
        <div class="file-info">
          <p class="file-name">
            {{ step.name }}
          </p>
          <p class="file-creation-info">Created on {{ formatDate(step.createdAt) }}</p>
        </div>
        <div class="users">
        </div>
        <div class="actions">
          <ts-button type="primary" size="small" filled @click="handleGoToLinkClick(step)">
            Open on Filestage
          </ts-button>
          <ts-button size="small" circle @click="handleLinkCopy(step)">
            <ts-icon icon="link" size="x-small" color="#6d7587" title="Copy" />
          </ts-button>
          <template v-if="isProjectManager">
            <EditProductionStep :users="users" :value="step" @input="handleStepChange(step, $event)" />
            <ts-button size="small" circle @click="handleStepDeleteClick(step)">
              <ts-icon icon="trash" size="x-small" color="#6d7587" title="Delete" />
            </ts-button>
          </template>
        </div>
      </div>
    </div>
  </ProjectScaffold>
</template>

<style lang="scss" scoped>
.header {
  display: flex;

  .titles {
    flex-grow: 1;
  }

  .title {
    font-family: Roboto;
    font-size: 20px;
    color: #6d7587;
  }

  .subtitle {
    font-family: Roboto;
    font-size: 12px;
    color: #6d7587;

    margin-top: 12px;
  }

  .actions {
    flex-shrink: 0;
    margin-left: 50px;
    display: flex;

    > * {
      margin-left: 10px;
    }
  }
}

.list-actions {
  margin-bottom: 20px;
  display: flex;

  .select {
    flex-grow: 1;

    > * {
      margin-right: 23px;
    }
  }

  .actions {
    > * {
      margin-left: 8px;
    }
  }
}

.list-header {
  font-family: Roboto;
  font-size: 10px;
  color: #9aa5b7;

  display: flex;

  margin-bottom: 10px;

  .name-header {
    margin-left: 48px;
    width: 400px;
  }
}

.row {
  background: #ebecf0;
  background-image: linear-gradient(-42deg, rgba(0, 0, 0, 0.4) 0%, rgba(255, 255, 255, 0.4) 100%);
  background-blend-mode: soft-light, normal;
  background-clip: padding-box;
  border: 2px solid rgba(255, 255, 255, 0.16);
  box-shadow: -2px -2px 6px 0 rgba(250, 251, 255, 0.48), 2px 2px 6px 0 rgba(166, 171, 189, 0.24);
  border-radius: 16px;
  margin-bottom: 10px;

  display: flex;
  align-items: center;
  padding: 22px 18px;

  .checkbox {
    margin-right: 13px;
  }

  .file-info {
    width: 400px;
  }

  .file-name {
    font-family: Roboto;
    font-size: 16px;
    color: #6d7587;
  }

  .file-creation-info {
    font-family: Roboto;
    font-size: 10px;
    line-height: 16px;
    color: #acbacc;
  }

  .users {
    flex-grow: 1;
  }

  .actions {
    display: flex;
    align-items: center;

    > * {
      margin-left: 10px;
    }
  }
}
</style>

<script>
import { mapState, mapActions } from 'vuex';

import ProjectScaffold from '../ProjectScaffold.vue';
import UserPicker from '@/components/inputs/UserPicker.vue';
import CreateProductionSteps from './CreateProductionSteps.vue';
import EditProductionStep from './EditProductionStep.vue';

import projectViewHelpers from '@/mixins/projectViewHelpers';

export default {
  mixins: [projectViewHelpers],
  components: {
    ProjectScaffold,
    UserPicker,
    CreateProductionSteps,
    EditProductionStep
  },
  data() {
    return {
      selectedSteps: []
    };
  },
  computed: {
    ...mapState('workflow', {
      users: state => state.currentProject.value?.users || []
    }),
    ...mapState('workflow/production', {
      loadingProductionSteps: state => state.steps.loading,
      productionStepsError: state => state.steps.error,
      productionSteps: state => state.steps.items
    })
  },
  methods: {
    ...mapActions('workflow/production', [
      'fetchCurrentProjectProductionSteps',
      'addCurrentProjectProductionStep',
      'updateCurrentProjectProductionStep',
      'updateCurrentProjectProductionStepUsers',
      'removeCurrentProjectProductionStep'
    ]),
    isStepSelected(step) {
      return !!this.selectedSteps.find(s => s.id === step.id);
    },
    handleSelectAllClick() {
      this.selectedSteps = this.productionSteps;
    },
    handleUnselectAllClick() {
      this.selectedSteps = [];
    },
    handleStepSelectedChange(step, value) {
      if (value) {
        this.selectedSteps = [...this.selectedSteps, step];
      } else {
        this.selectedSteps = this.selectedSteps.filter(s => s.id !== step.id);
      }
    },
    handleStepsCreate(steps) {
      steps.forEach(step => {
        this.addCurrentProjectProductionStep({
          step: {
            ...step,
            createdAt: new Date().toISOString()
          }
        }).catch(() => this.showError('Error when creating production step'));
      });
    },
    handleDeleteAllClick() {
      this.selectedSteps.forEach(step => {
        this.removeCurrentProjectProductionStep({ step });
      });
    },
    handleGoToLinkClick(step) {
      window.open(step.url, '_blank');
    },
    async handleLinkCopy(step) {
      try {
        await this.$copyText(step.url);
        this.showInfo('Link copied to clipboard');
      } catch (err) {
        console.error('Error when copying step link', err);
        this.showError('Error when copying step link');
      }
    },
    handleStepChange(step, value) {
      this.updateCurrentProjectProductionStep({ step: value });
    },
    handleStepUsersChange(step, value) {
      this.updateCurrentProjectProductionStepUsers({
        step: { ...step, users: value }
      });
    },
    handleStepDeleteClick(step) {
      this.removeCurrentProjectProductionStep({ step });
    }
  },
  async mounted() {
    this.fetchCurrentProjectProductionSteps();
  },
  watch: {
    productionStepsError(value) {
      if (value) {
        this.showError(value);
      }
    }
  }
};
</script>
